export default {
  data() {
    return {
      columnWidths: {},
      isResizing: false,
      resizingColumn: null,
      startX: 0,
      startWidth: 0,
      tableId: null,
      minColumnWidth: 80, // pixels
      maxColumnWidth: 600, // pixels
      resizeHandleWidth: 8, // pixels
    };
  },

  computed: {
    resizableTableClass() {
      return {
        'resizable-table': true,
        'is-resizing': this.isResizing,
      };
    },

    // Generate column styles based on saved widths
    columnStyles() {
      const styles = {};
      Object.keys(this.columnWidths).forEach((key) => {
        if (this.columnWidths[key]) {
          styles[key] = {
            width: `${this.columnWidths[key]}px`,
            minWidth: `${this.columnWidths[key]}px`,
            maxWidth: `${this.columnWidths[key]}px`,
          };
        }
      });
      return styles;
    },
  },

  methods: {
    // Initialize resizable columns
    initResizableColumns(tableId) {
      this.tableId = tableId || this.$route.name || 'default-table';
      this.loadColumnWidths();

      this.$nextTick(() => {
        this.setupResizeHandles();
        this.applyColumnWidths();
      });
    },

    // Setup resize handles on table headers
    setupResizeHandles() {
      const table = this.$el.querySelector('.b-table');
      if (!table) return;

      const headers = table.querySelectorAll('thead th');
      headers.forEach((header, index) => {
        // Skip if column is marked as non-resizable
        const field = this.fields[index];
        if (field && field.nonResizable) return;

        // Remove existing handle if any
        const existingHandle = header.querySelector('.resize-handle');
        if (existingHandle) {
          existingHandle.remove();
        }

        // Create resize handle
        const handle = document.createElement('div');
        handle.className = 'resize-handle';
        handle.style.cssText = `
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: ${this.resizeHandleWidth}px;
          cursor: col-resize;
          user-select: none;
          z-index: 10;
        `;

        // Add hover effect
        handle.addEventListener('mouseenter', () => {
          handle.style.backgroundColor = 'rgba(59, 130, 246, 0.3)';
        });
        handle.addEventListener('mouseleave', () => {
          if (!this.isResizing) {
            handle.style.backgroundColor = 'transparent';
          }
        });

        // Mouse events
        handle.addEventListener('mousedown', (e) =>
          this.startResize(e, header, field?.key || `col-${index}`),
        );

        // Touch events
        handle.addEventListener(
          'touchstart',
          (e) => this.startResize(e, header, field?.key || `col-${index}`),
          { passive: false },
        );

        header.style.position = 'relative';
        header.appendChild(handle);
      });
    },

    // Start column resize
    startResize(e, header, columnKey) {
      e.preventDefault();
      e.stopPropagation();

      this.isResizing = true;
      this.resizingColumn = columnKey;

      // Get initial values
      const touch = e.type.includes('touch') ? e.touches[0] : e;
      this.startX = touch.clientX;
      this.startWidth = header.offsetWidth;

      // Add event listeners
      if (e.type.includes('touch')) {
        document.addEventListener('touchmove', this.handleResize, {
          passive: false,
        });
        document.addEventListener('touchend', this.stopResize);
      } else {
        document.addEventListener('mousemove', this.handleResize);
        document.addEventListener('mouseup', this.stopResize);
      }

      // Add resizing class to body to change cursor globally
      document.body.classList.add('col-resizing');
    },

    // Handle resize movement
    handleResize(e) {
      if (!this.isResizing) return;

      e.preventDefault();
      const touch = e.type.includes('touch') ? e.touches[0] : e;
      const deltaX = touch.clientX - this.startX;
      const newWidth = Math.max(
        this.minColumnWidth,
        Math.min(this.maxColumnWidth, this.startWidth + deltaX),
      );

      // Update column width
      this.$set(this.columnWidths, this.resizingColumn, newWidth);

      // Apply width immediately for visual feedback
      this.applyColumnWidth(this.resizingColumn, newWidth);
    },

    // Stop resizing
    stopResize() {
      if (!this.isResizing) return;

      this.isResizing = false;
      this.resizingColumn = null;

      // Remove event listeners
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.removeEventListener('touchmove', this.handleResize);
      document.removeEventListener('touchend', this.stopResize);

      // Remove resizing class
      document.body.classList.remove('col-resizing');

      // Save column widths
      this.saveColumnWidths();

      // Reapply all widths to ensure consistency
      this.$nextTick(() => {
        this.applyColumnWidths();
      });
    },

    // Apply width to specific column
    applyColumnWidth(columnKey, width) {
      const table = this.$el.querySelector('.b-table');
      if (!table) return;

      // Find column index
      const fieldIndex = this.fields.findIndex((f) => f.key === columnKey);
      if (fieldIndex === -1) return;

      // Apply to header
      const headers = table.querySelectorAll(
        `thead th:nth-child(${fieldIndex + 1})`,
      );
      headers.forEach((header) => {
        header.style.width = `${width}px`;
        header.style.minWidth = `${width}px`;
        header.style.maxWidth = `${width}px`;
      });

      // Apply to cells
      const cells = table.querySelectorAll(
        `tbody td:nth-child(${fieldIndex + 1})`,
      );
      cells.forEach((cell) => {
        cell.style.width = `${width}px`;
        cell.style.minWidth = `${width}px`;
        cell.style.maxWidth = `${width}px`;
      });
    },

    // Apply all column widths
    applyColumnWidths() {
      Object.keys(this.columnWidths).forEach((columnKey) => {
        if (this.columnWidths[columnKey]) {
          this.applyColumnWidth(columnKey, this.columnWidths[columnKey]);
        }
      });
    },

    // Save column widths to localStorage
    saveColumnWidths() {
      if (!this.tableId) return;

      const key = `table_column_widths_${this.tableId}`;
      localStorage.setItem(key, JSON.stringify(this.columnWidths));
    },

    // Load column widths from localStorage
    loadColumnWidths() {
      if (!this.tableId) return;

      const key = `table_column_widths_${this.tableId}`;
      const saved = localStorage.getItem(key);

      if (saved) {
        try {
          const widths = JSON.parse(saved);
          // Validate widths
          Object.keys(widths).forEach((columnKey) => {
            if (this.fields.some((f) => f.key === columnKey)) {
              this.$set(this.columnWidths, columnKey, widths[columnKey]);
            }
          });
        } catch (e) {
          console.error('Error loading column widths:', e);
        }
      }
    },

    // Reset column widths
    resetColumnWidths() {
      this.columnWidths = {};
      this.saveColumnWidths();

      // Remove inline styles
      const table = this.$el.querySelector('.b-table');
      if (table) {
        const elements = table.querySelectorAll('th, td');
        elements.forEach((el) => {
          el.style.width = '';
          el.style.minWidth = '';
          el.style.maxWidth = '';
        });
      }

      // Re-setup handles
      this.$nextTick(() => {
        this.setupResizeHandles();
      });
    },

    // Auto-fit column to content
    autoFitColumn(columnKey) {
      const table = this.$el.querySelector('.b-table');
      if (!table) return;

      const fieldIndex = this.fields.findIndex((f) => f.key === columnKey);
      if (fieldIndex === -1) return;

      // Get all cells in column
      const cells = table.querySelectorAll(
        `th:nth-child(${fieldIndex + 1}), td:nth-child(${fieldIndex + 1})`,
      );
      let maxWidth = this.minColumnWidth;

      cells.forEach((cell) => {
        // Temporarily remove width constraints
        const originalWidth = cell.style.width;
        cell.style.width = 'auto';
        cell.style.minWidth = 'auto';
        cell.style.maxWidth = 'auto';

        // Measure natural width
        const naturalWidth = cell.offsetWidth;
        maxWidth = Math.max(maxWidth, naturalWidth + 20); // Add padding

        // Restore constraints
        cell.style.width = originalWidth;
      });

      // Apply new width
      const finalWidth = Math.min(this.maxColumnWidth, maxWidth);
      this.$set(this.columnWidths, columnKey, finalWidth);
      this.applyColumnWidth(columnKey, finalWidth);
      this.saveColumnWidths();
    },
  },

  mounted() {
    // Re-setup resize handles after table updates
    this.$watch(
      'fields',
      () => {
        this.$nextTick(() => {
          this.setupResizeHandles();
          this.applyColumnWidths();
        });
      },
      { deep: true },
    );

    // Re-apply widths after data changes
    this.$watch(
      'items',
      () => {
        this.$nextTick(() => {
          this.applyColumnWidths();
        });
      },
      { deep: true },
    );
  },

  beforeDestroy() {
    // Cleanup
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);
    document.removeEventListener('touchmove', this.handleResize);
    document.removeEventListener('touchend', this.stopResize);
    document.body.classList.remove('col-resizing');
  },
};
